import React, { useRef, useEffect } from 'react';
import { useSceneScrollTimings } from '../../hooks/useSceneScrollTimings';
import { MoneyCounter } from '../shared/MoneyCounter';
import { CompassLottie } from '../lottie/CompassLottie';
import { ArrowDown } from 'lucide-react';
import { gsap } from 'gsap';

/**
 * Scene 0 - Orientation (ENHANCED UI)
 * 
 * Added: Parallax effects, staggered animations, floating elements
 */
export const Scene0Orientation = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const parallaxRef = useRef(null);

  // Track scene entry/exit
  useSceneScrollTimings(sectionRef, {
    start: 'top center',
    end: 'bottom center',
    onEnter: () => {
      if (onSceneChange) onSceneChange(0);
    },
    onUpdate: (progress) => {
      if (onProgressUpdate) onProgressUpdate(progress * 5);
      
      // Parallax effect on background
      if (parallaxRef.current) {
        const yOffset = progress * 100;
        parallaxRef.current.style.transform = `translateY(${yOffset}px)`;
      }
    },
  });

  // Enhanced entrance animations with stagger - MOBILE SAFE
  useEffect(() => {
    if (!contentRef.current) return;

    // Check if animations should run (desktop only for performance)
    const shouldAnimate = window.innerWidth >= 640; // sm breakpoint
    
    if (!shouldAnimate) {
      // On mobile, just ensure everything is visible
      const elements = contentRef.current.querySelectorAll('.orientation-element');
      elements.forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    const ctx = gsap.context(() => {
      // Staggered fade-in with scale - ensure elements END at visible state
      gsap.fromTo('.orientation-element', 
        {
          opacity: 0,
          y: 40,
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          delay: 0.3,
        }
      );

      // Enhanced scroll prompt with bounce - keep visible
      gsap.to('.scroll-prompt', {
        y: 12,
        duration: 1.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
      
      // Floating animation on compass
      gsap.to('.compass-float', {
        y: -15,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }, contentRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="orientation"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="orientation-section"
    >
      {/* Animated GIF Background - Desktop (16:9) */}
      <div className="absolute inset-0 hidden md:block">
        <img
          src="/hero-desktop.gif"
          alt=""
          className="w-full h-full object-cover"
          style={{ opacity: 1 }}
        />
        {/* MUCH LIGHTER overlay - let the golden compass shine through! */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/40 via-transparent to-transparent" />
      </div>

      {/* Animated GIF Background - Mobile (9:16) */}
      <div className="absolute inset-0 md:hidden">
        <img
          src="/hero-mobile.gif"
          alt=""
          className="w-full h-full object-cover"
          style={{ opacity: 1 }}
        />
        {/* Light overlay for mobile text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/30 via-transparent to-navy-900/40" />
      </div>

      {/* Fallback gradient (if GIFs don't load) */}
      <div className="absolute inset-0 bg-gradient-orientation hidden" />

      <div ref={contentRef} className="max-w-7xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-24 relative z-10">
        {/* Content - Left aligned with strong backgrounds */}
        <div className="max-w-2xl">
          {/* Headline - MAXIMUM CONTRAST */}
          <h1
            className="orientation-element text-4xl sm:text-5xl lg:text-7xl font-display font-bold text-white tracking-tight leading-[1.1] mb-6"
            style={{ 
              textShadow: '0 8px 32px rgba(0,0,0,1), 0 4px 16px rgba(0,0,0,0.9), 0 2px 8px rgba(0,0,0,0.8)'
            }}
            data-testid="orientation-headline"
          >
            Understanding isn't extra.
          </h1>

          {/* Subheadline - BRIGHT TEAL with strong shadow */}
          <p
            className="orientation-element text-xl sm:text-2xl lg:text-4xl font-display font-semibold text-teal-200 mb-12 leading-tight"
            style={{ 
              textShadow: '0 6px 24px rgba(0,0,0,1), 0 3px 12px rgba(0,0,0,0.9), 0 0 40px rgba(17,197,217,0.6)'
            }}
            data-testid="orientation-subheadline"
          >
            It's earnings in disguise.
          </p>

          {/* Counter Section - COMPACT & ELEGANT with SOFTER GOLD */}
          <div className="orientation-element mt-8 sm:mt-10">
            {/* Compact card with better proportions */}
            <div className="inline-block max-w-md">
              {/* Label - outside card for better layout */}
              <p
                className="text-xs sm:text-sm text-teal-200/90 font-sans uppercase tracking-[0.25em] mb-3 font-semibold"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,1)' }}
                data-testid="counter-label"
              >
                Money left on the table
              </p>
              
              {/* Minimal glass card - just for the number */}
              <div className="px-5 sm:px-6 py-4 sm:py-5 rounded-xl bg-black/10 backdrop-blur-md border border-white/5">
                <MoneyCounter
                  end={50000}
                  duration={3}
                  className="text-5xl sm:text-6xl lg:text-7xl font-bold"
                  style={{
                    color: '#D4AF37',
                    textShadow: '0 3px 16px rgba(212,175,55,0.7), 0 1px 8px rgba(0,0,0,1), 0 0 50px rgba(212,175,55,0.3)',
                    letterSpacing: '-0.02em'
                  }}
                  testId="orientation-counter"
                />
              </div>

              {/* Supporting text - outside card */}
              <p
                className="text-xs sm:text-sm text-white/80 font-sans leading-relaxed mt-3 max-w-sm"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,1)' }}
                data-testid="counter-context"
              >
                Every year without strategy, value quietly slips away
              </p>
            </div>
          </div>

          {/* Scroll Prompt - BRIGHT WHITE */}
          <div
            className="orientation-element scroll-prompt mt-16 flex flex-col items-start gap-3"
            data-testid="scroll-prompt"
          >
            <p 
              className="text-base sm:text-lg text-white font-sans tracking-wide font-semibold"
              style={{ textShadow: '0 4px 16px rgba(0,0,0,1), 0 2px 8px rgba(0,0,0,0.8), 0 0 30px rgba(17,197,217,0.4)' }}
            >
              Scroll to see your map
            </p>
            <div className="relative">
              <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-lg animate-pulse" />
              <ArrowDown
                className="relative w-6 h-6 text-teal-300"
                strokeWidth={2}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};